import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class AiRequestGuardService {
  private readonly replayWindowMs = 3000;
  private readonly guardedPath = '/lumos-ai';
  private readonly unsafeMethods = new Set(['POST', 'PUT', 'PATCH']);
  private readonly requests = new Map<string, Observable<unknown>>();

  shouldGuard(method: string, url: string): boolean {
    return (
      this.unsafeMethods.has(method.toUpperCase()) &&
      this.normaliseUrl(url).includes(this.guardedPath)
    );
  }

  run<T>(
    method: string,
    url: string,
    body: unknown,
    requestFactory: () => Observable<T>
  ): Observable<T> {
    const key = this.createKey(method, url, body);
    const existingRequest = this.requests.get(key);

    if (existingRequest) {
      return existingRequest as Observable<T>;
    }

    let failed = false;
    const request$ = requestFactory().pipe(
      tap({ error: () => (failed = true) }),
      finalize(() => {
        if (failed) {
          this.requests.delete(key);
          return;
        }

        window.setTimeout(() => this.requests.delete(key), this.replayWindowMs);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.requests.set(key, request$);
    return request$;
  }

  private createKey(method: string, url: string, body: unknown): string {
    return [
      method.toUpperCase(),
      this.normaliseUrl(url),
      this.stableStringify(body),
    ].join('|');
  }

  private normaliseUrl(url: string): string {
    try {
      const parsedUrl = new URL(url, window.location.origin);
      return `${parsedUrl.pathname}${parsedUrl.search}`;
    } catch {
      return url;
    }
  }

  private stableStringify(value: unknown, seen = new WeakSet()): string {
    if (value === null || typeof value !== 'object') {
      return JSON.stringify(value);
    }

    if (value instanceof Date) {
      return value.toISOString();
    }

    if (value instanceof FormData) {
      return this.stringifyFormData(value);
    }

    if (seen.has(value as object)) {
      return '"[Circular]"';
    }

    seen.add(value as object);

    if (Array.isArray(value)) {
      return `[${value
        .map((item) => this.stableStringify(item, seen))
        .join(',')}]`;
    }

    return `{${Object.keys(value as Record<string, unknown>)
      .sort()
      .map((key) => {
        const objectValue = value as Record<string, unknown>;
        return `${JSON.stringify(key)}:${this.stableStringify(
          objectValue[key],
          seen
        )}`;
      })
      .join(',')}}`;
  }

  private stringifyFormData(formData: FormData): string {
    const values: string[] = [];

    formData.forEach((value, key) => {
      if (value instanceof File) {
        values.push(`${key}:file:${value.name}:${value.size}:${value.type}`);
      } else {
        values.push(`${key}:${value}`);
      }
    });

    return values.sort().join('|');
  }
}
